// src/pages/Register.jsx - Apartment Management System
import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiUserLine, RiBuildingLine, RiLoginBoxLine, RiErrorWarningLine, RiMailLine, RiSmartphoneLine, RiCommunityLine, RiShieldKeyholeLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { registerUser, verifyOTP, checkAvailability } from '../models/authModel';
import Navbar from './home/Navbar';
import AuthLanguageSelector from './components/AuthLanguageSelector';

const Register = () => {
  const { t, i18n } = useTranslation();

  const { isDarkMode , isRTL } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('register'); // 'register', 'pricing', or 'otp'
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState('');
  const [selectedTier, setSelectedTier] = useState(searchParams.get('tier') || 'starter');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    companyName: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [defaultCountry, setDefaultCountry] = useState('iq'); // Fallback location

  useEffect(() => {
    const tier = searchParams.get('tier');
    if (tier) setSelectedTier(tier);

    // Fetch location for default country code
    fetch('https://ipapi.co/json/').
    then((res) => res.json()).
    then((data) => {
      if (data.country_code) setDefaultCountry(data.country_code.toLowerCase());
    }).
    catch(() => console.log('Location detection failed'));
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear errors when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Clear register error when typing
    if (registerError) {
      setRegisterError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('first_name_required');
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = t('val_first_name_min');
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = t('val_first_name_max');
    }

    // Last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('last_name_required');
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = t('val_last_name_min');
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = t('val_last_name_max');
    }

    // Username: 3-30 chars, no whitespace (accepts any Unicode script)
    if (!formData.username.trim()) {
      newErrors.username = t('username_required');
    } else if (formData.username.trim().length < 3) {
      newErrors.username = t('val_username_min');
    } else if (formData.username.trim().length > 30) {
      newErrors.username = t('val_username_max');
    } else if (/\s/.test(formData.username)) {
      newErrors.username = t('val_username_no_spaces');
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = t('email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t('val_email_invalid');
    }

    // Company name
    if (!formData.companyName.trim()) {
      newErrors.companyName = t('company_name_required');
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = t('val_company_name_min');
    }

    // Password
    if (!formData.password) {
      newErrors.password = t('password_required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('val_password_min');
    } else if (formData.password.length > 128) {
      newErrors.password = t('val_password_max');
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwords_do_not_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setRegisterError('');

    try {
      const response = await checkAvailability(formData.email, formData.username, formData.phone);
      if (response.success) {
        setStep('pricing');
      }
    } catch (error) {
      console.error('Availability check error:', error);
      const field = error.field;
      if (field) setErrors(prev => ({ ...prev, [field]: error.message }));
      setRegisterError(error.message || t('registration_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalRegister = async () => {
    setIsLoading(true);
    setRegisterError('');

    try {
      const response = await registerUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        password: formData.password,
        subscriptionTier: selectedTier
      });

      if (response.success) {
        if (response.requireOtp) {
          setStep('otp');
          setUserId(response.userId);
          setRegisterError('');
        } else {
          // Fallback for immediate login (if configured that way)
          login(response.user, response.token);
          navigate('/admin/dashboard');
        }
      } else {
        const field = response.field;
        if (field) setErrors(prev => ({ ...prev, [field]: response.message }));
        setRegisterError(response.message || t('registration_failed'));
        setStep('register');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errData = error.response?.data || error;
      const field = errData?.field;
      if (field) setErrors(prev => ({ ...prev, [field]: errData.message }));
      setRegisterError(errData?.message || error.message || t('registration_failed'));
      setStep('register');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length < 6) {
      setRegisterError(t('enter_valid_otp'));
      return;
    }

    setIsLoading(true);
    setRegisterError('');

    try {
      const response = await verifyOTP(userId, otp);

      if (response.success) {
        login(response.user, response.token);
        navigate('/admin/dashboard');
      } else {
        setRegisterError(response.message || t('verification_failed'));
      }
    } catch (error) {
      console.error('OTP error:', error);
      setRegisterError(error.message || t('verification_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const pricingList = [
  { id: 'starter', name: 'Starter', price: '200', units: '100', features: ['Double-Entry Ledger', 'Basic Reports', 'Maintenance Tickets'] },
  { id: 'pro', name: 'Pro', price: '300', units: '150', features: ['Global Maintenance Hub', 'WhatsApp Reminders', 'Automated Owner Statements'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', units: 'Unlimited', features: ['Tenant & Owner Portals', 'Two-Way Messaging', 'Stripe E2E Payments'] }];


  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDarkMode ? 'bg-zinc-950' : 'bg-white'}`}>
            <Navbar />
            <div className={`max-w-2xl w-full relative z-10 ${isDarkMode ? 'bg-zinc-900 border-white/[0.06]' : 'bg-white border-black/[0.1]'} border rounded-xl shadow-2xl overflow-hidden transition-all duration-300`}>
                <AuthLanguageSelector />
                <div className="p-6 sm:p-8">
                    <div className="text-center mb-8">
                        <div className="mb-4">
                            <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="QuadCores Logo"
                className="h-16 w-auto mx-auto" />
              
                        </div>
                        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            {step === 'otp' ? t('verify_phone_number') : 'Quadcores Properties'}
                        </h1>
                        <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                            {step === 'otp' ?
              t('verification_code_sent_to_phone') :
              t('sign_up_to_get_started')}
                        </p>
                    </div>

                    {registerError &&
          <div className={`mb-6 p-3 rounded-lg flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-100 text-red-700'}`
          }>
                            <RiErrorWarningLine className="me-2 flex-shrink-0" />
                            <span>{registerError}</span>
                        </div>
          }

                    {step === 'register' ?
          <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* First Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('first_name')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.firstName ? 'border-red-500' : ''}`}
                    placeholder={t('enter_first_name')} />
                  
                                    </div>
                                    {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('last_name')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.lastName ? 'border-red-500' : ''}`}
                    placeholder={t('enter_last_name')} />
                  
                                    </div>
                                    {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                                </div>

                                {/* Username */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('username')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type="text"
                    name="username"
                    dir="ltr"
                    value={formData.username}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.username ? 'border-red-500' : ''}`}
                    placeholder={t('enter_username')} />
                  
                                    </div>
                                    {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('email')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiMailLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type="email"
                    name="email"
                    dir="ltr"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.email ? 'border-red-500' : ''}`}
                    placeholder={t('enter_email')} />
                  
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('phone')}
                                    </label>
                                    <div dir="ltr">
                                        <PhoneInput
                    country={defaultCountry}
                    value={formData.phone}
                    onChange={(phone) => handleChange({ target: { name: 'phone', value: '+' + phone } })}
                    enableSearch={true}
                    disableSearchIcon={true}
                    buttonStyle={{
                      backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                    }}
                    inputStyle={{
                      width: '100%',
                      backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      color: isDarkMode ? '#ffffff' : '#000000',
                      paddingTop: '23px', paddingBottom: '23px', borderRadius: '8px'
                    }}
                    dropdownStyle={{
                      backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
                      color: isDarkMode ? '#ffffff' : '#000000'
                    }} />
                  
                                    </div>
                                </div>

                                {/* Company Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('company_name')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiCommunityLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-3 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.companyName ? 'border-red-500' : ''}`}
                    placeholder={t('enter_company_name')} />
                  
                                    </div>
                                    {errors.companyName && <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>}
                                </div>

                                {/* Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('password')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    dir="ltr"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-10 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.password ? 'border-red-500' : ''}`}
                    placeholder={t('enter_password')} />

                                        <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className={`absolute inset-y-0 end-0 pe-3 flex items-center ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`}>
                    
                                            {showPassword ? <RiEyeOffLine size={18} /> : <RiEyeLine size={18} />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('confirm_password')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                                            <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    dir="ltr"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`block w-full ps-10 pe-10 py-3 ${isDarkMode ?
                    'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                    'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder={t('confirm_password')} />

                                        <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className={`absolute inset-y-0 end-0 pe-3 flex items-center ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`}>
                    
                                            {showConfirmPassword ? <RiEyeOffLine size={18} /> : <RiEyeLine size={18} />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
                                </div>
                            </div>

                            <div>
                                <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 ${isDarkMode ?
                'bg-red-600 hover:bg-red-700' :
                'bg-red-600 hover:bg-red-700'} text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`
                }>
                
                                    {isLoading ?
                <>
                                            <svg className="animate-spin -ms-1 me-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('next_step')}
                                        </> :

                <>
                                            <RiLoginBoxLine className="me-2" />
                                            {t("continue_to_plans")}
                                        </>
                }
                                </button>
                            </div>

                            <div className="text-center mt-4">
                                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                                    {t('already_have_account')} {' '}
                                    <Link to="/login" className={`font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                                        {t('login')}
                                    </Link>
                                </p>
                            </div>
                        </form> :
          step === 'pricing' ?
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {pricingList.map((plan) => {return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedTier(plan.id)}
                    className={`cursor-pointer relative p-6 rounded-2xl border-2 transition-all duration-300 ${selectedTier === plan.id ?
                    'border-red-600 bg-red-600/5 ring-4 ring-red-600/10' :
                    isDarkMode ? 'border-white/10 bg-zinc-800/50 hover:border-white/20' : 'border-black/5 bg-zinc-50 hover:border-black/10'}`
                    }>
                    
                                        {plan.popular &&
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-tighter">
                                                {t("popular")}
                                            </span>
                    }
                                        <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{plan.name}</h3>
                                        <div className="mb-4">
                                            <span className="text-3xl font-bold">${plan.price}</span>
                                            {plan.price !== 'Custom' && <span className="text-sm opacity-50 ms-1">{t("mo_4")}</span>}
                                        </div>
                                        <ul className="space-y-3 mb-6 opacity-70">
                                            <li className="text-xs flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
                                                {t("up_to")} {plan.units} {t("units_2")}
                                            </li>
                                            {plan.features.slice(0, 2).map((f, i) =>
                      <li key={i} className="text-xs flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
                                                    {f}
                                                </li>
                      )}
                                        </ul>
                                    </div>);}
              )}
                            </div>

                            <div className="flex flex-col gap-4">
                                <button
                onClick={handleFinalRegister}
                disabled={isLoading}
                className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-xl shadow-red-600/20 flex items-center justify-center transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50">
                
                                    {isLoading ? t('processing') : t('complete_registration')}
                                </button>
                                <button
                onClick={() => setStep('register')}
                className="text-sm opacity-50 hover:opacity-100 transition-opacity">
                                    {t("back_to_info")}
                                
              </button>
                            </div>
                        </div> :

          <form onSubmit={handleOtpSubmit} className="space-y-6">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                    {t("verification_code_1")}
                                </label>
                                <div className="relative">
                                    <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className={`block w-full px-3 py-3 text-center text-2xl tracking-widest ${isDarkMode ?
                  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`
                  }
                  placeholder="123456"
                  maxLength={6} />
                
                                </div>
                                <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                                    {t("please_enter_the_6_digit_code_sent_to")} <strong>{formData.phone}</strong>
                                </p>
                            </div>

                            <div>
                                <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 ${isDarkMode ?
                'bg-red-600 hover:bg-red-700' :
                'bg-red-600 hover:bg-red-700'} text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`
                }>
                
                                    {isLoading ?
                <>
                                            <svg className="animate-spin -ms-1 me-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('verifying')}
                                        </> :

                <>
                                            <RiShieldKeyholeLine className="me-2" />
                                            {t("verify_phone")}
                                        </>
                }
                                </button>
                            </div>
                        </form>
          }
                </div>
            </div>
        </div>);

};

export default Register;